/**
 * 组织结构图组件
 * 使用 CSS Tree 实现组织架构可视化
 */

import React from 'react';
import { Empty, Tooltip, Card } from 'antd';
import {
  ApartmentOutlined,
  TeamOutlined,
  BankOutlined,
  ClusterOutlined,
  GroupOutlined,
} from '@ant-design/icons';
import type { OrganizationTreeNode } from '../../api/types';
import { OrganizationType } from '../../api/types';
import styles from './OrganizationChart.module.less';

/** 组织类型颜色映射 */
const typeColors: Record<number, string> = {
  [OrganizationType.COMPANY]: '#1890ff',
  [OrganizationType.DEPARTMENT]: '#52c41a',
  [OrganizationType.GROUP]: '#faad14',
  [OrganizationType.TEAM]: '#722ed1',
};

/** 组织类型图标映射 */
const typeIcons: Record<number, React.ReactNode> = {
  [OrganizationType.COMPANY]: <BankOutlined />,
  [OrganizationType.DEPARTMENT]: <ClusterOutlined />,
  [OrganizationType.GROUP]: <GroupOutlined />,
  [OrganizationType.TEAM]: <TeamOutlined />,
};

/** 组织类型文本 */
const typeLabels: Record<number, string> = {
  [OrganizationType.COMPANY]: '公司',
  [OrganizationType.DEPARTMENT]: '部门',
  [OrganizationType.GROUP]: '分组',
  [OrganizationType.TEAM]: '团队',
};

interface OrganizationChartProps {
  data: OrganizationTreeNode[];
  onNodeClick?: (node: OrganizationTreeNode) => void;
}

/** 单个节点组件 */
const TreeNode: React.FC<{
  node: OrganizationTreeNode;
  onNodeClick?: (node: OrganizationTreeNode) => void;
}> = ({ node, onNodeClick }) => {
  const typeColor = typeColors[node.type] || '#1890ff';
  const icon = typeIcons[node.type] || <ApartmentOutlined />;

  return (
    <li>
      <Tooltip
        title={
          <div>
            <div><strong>{node.title}</strong></div>
            <div>类型: {typeLabels[node.type] || '未知'}</div>
            <div>成员数: {node.memberCount}</div>
          </div>
        }
      >
        <Card
          size="small"
          className={styles.nodeCard}
          style={{
            borderLeftColor: typeColor,
            borderLeftWidth: 4,
            cursor: 'pointer',
          }}
          onClick={() => onNodeClick?.(node)}
        >
          <div className={styles.nodeContent}>
            <span className={styles.nodeIcon} style={{ color: typeColor }}>
              {icon}
            </span>
            <span className={styles.nodeName}>{node.title}</span>
            <span className={styles.childCount}>
              ({node.memberCount})
            </span>
          </div>
        </Card>
      </Tooltip>
      {node.children && node.children.length > 0 && (
        <ul>
          {node.children.map((child) => (
            <TreeNode key={child.key} node={child} onNodeClick={onNodeClick} />
          ))}
        </ul>
      )}
    </li>
  );
};

const OrganizationChart: React.FC<OrganizationChartProps> = ({
  data,
  onNodeClick,
}) => {
  if (!data || data.length === 0) {
    return (
      <Empty
        description="暂无组织数据"
        style={{ padding: '80px 0' }}
      />
    );
  }

  return (
    <div className={styles.treeContainer}>
      <div className={styles.tree}>
        <ul>
          {data.map((node) => (
            <TreeNode key={node.key} node={node} onNodeClick={onNodeClick} />
          ))}
        </ul>
      </div>
    </div>
  );
};

export default OrganizationChart;
